import { ArrowCcwIcon, CloseIcon } from "@xola/icons";
import { Tag, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, SecondaryButton } from "common";
import { isEqual } from "lodash";
import React from "react";
import shallow from "zustand/shallow";
import { usePurchaseFilters } from "../../hooks/usePurchaseFilters";
import { DateType, usePurchaseFilterStore } from "../../stores/purchaseFilterStore";
import { ResourceSelectionState } from "../../types/Dashboard";
import { isPurchaseFilterApplied } from "./PurchaseFilterList";

export const PurchaseFilterTagList = () => {
    const [from, to] = usePurchaseFilterStore((state) => [state.from, state.to], shallow);
    const [filters, resetToDefaults] = usePurchaseFilterStore(
        (state) => [state.filters, state.resetToDefaults, state.resetDate],
        shallow,
    );

    const dateText = filters.date === DateType.ARRIVAL_DATE ? "Arrival Date" : "Purchase Date";
    const FiltersList = usePurchaseFilters();

    const showFilterTagList = (from && to) || FiltersList.some((item) => isPurchaseFilterApplied(item.value));

    return (
        <div className="flex w-full items-center justify-between space-x-1 border-b border-gray-light py-2 px-10">
            <span className="my-1.5 min-w-min flex-shrink-0 font-bold">Search:</span>

            <span className="w-0 flex-grow truncate">
                {showFilterTagList ? (
                    <div className="flex flex-wrap items-center gap-2 leading-4">
                        {from && to && (
                            <Tag key={filters.date} size="large" className="items-center space-x-1">
                                <Label label={dateText} />
                                &nbsp;is between
                                <span className="truncate font-bold">
                                    {formatDate(from, DateFormat.DATE)}
                                    &nbsp;-&nbsp;
                                    {formatDate(to, DateFormat.DATE)}
                                </span>
                            </Tag>
                        )}

                        {FiltersList.map((item) =>
                            isPurchaseFilterApplied(item.value) ? (
                                <FilterTag
                                    key={item.label}
                                    localize={item.localize}
                                    label={item.label}
                                    filter={item.value}
                                    data={item.data}
                                    onReset={item.onReset}
                                />
                            ) : null,
                        )}
                    </div>
                ) : (
                    "No Search applied"
                )}
            </span>
            <span className="min-w-min flex-shrink-0">
                {showFilterTagList && (
                    <SecondaryButton size="small" onClick={resetToDefaults}>
                        <ArrowCcwIcon className="mr-1" />
                        Reset Search
                    </SecondaryButton>
                )}
            </span>
        </div>
    );
};

interface FilterTagProps {
    label: string;
    localize?: boolean;
    onReset: () => void;
    filter: ResourceSelectionState;
    data: any[];
}

/**
 * Returning as spans because localisation works only on individual words
 * not on sentences joined by ,
 */
const getTagText = (tags: string[]) => {
    const { length } = tags;
    const elements = tags.map((tag, index) => {
        const element = <span>{tag}</span>;
        if (index < length - 1) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                    {element}
                    <span>, </span>
                </React.Fragment>
            );
        }

        return element;
    });
    return elements;
};

const FilterTag = ({ label, localize = true, onReset, filter, data }: FilterTagProps) => {
    const selectedItems = filter.selected.map((item) => {
        const selectedItem = data.find((index) => {
            if (Array.isArray(index.id) && Array.isArray(item)) {
                return isEqual(index.id, item);
            }

            if (!Array.isArray(index.id) && !Array.isArray(item)) {
                return index.id === item;
            }

            return false;
        });

        return selectedItem ? selectedItem.name : "";
    });

    const isItemSelected = selectedItems.length > 0;
    const tags = isItemSelected ? selectedItems : filter.all ? ["All"] : ["Unassigned"];

    return (
        <Tooltip
            content={
                <div className={clsx("space-y-1 py-1", { "no-translate": !localize })}>
                    {isItemSelected ? selectedItems.map((item) => <div key={item}>{item}</div>) : <div>{tags}</div>}
                </div>
            }
        >
            <Tag size="large" className="items-center space-x-1">
                <Label label={label} />:
                <span
                    className={clsx("max-w-50 overflow-hidden text-ellipsis whitespace-nowrap", {
                        "no-translate": !localize,
                    })}
                >
                    {getTagText(tags)}
                </span>
                <span className="cursor-pointer">
                    <CloseIcon onClick={onReset} />
                </span>
            </Tag>
        </Tooltip>
    );
};

interface LabelProps {
    label: string;
}

const Label = ({ label }: LabelProps) => {
    return <span className="font-bold">{label}</span>;
};
